import onReady from '../utils/onReady';
import * as Events from '../utils/events';
import * as ClassList from '../utils/classList';
import domIterator from '../utils/domIterator';
import * as formError from '../utils/formError';
import triggerCustomEvent from '../utils/triggerCustomEvent';

let apiUrlAdd = '/api/managebasket/addleasetobasket';
let apiUrlRemove = '/api/managebasket/removeleasefrombasket';

/**
 *
 * @param {HTMLElement} container
 * @constructor
 */
class AddToBasket {

    /**
     * The constructor is fired once the class is instantiated.
     *
     * @param {HTMLElement} container
     */
    constructor(container) {
        // create link to basket. This is inserted and removed dynamically
        this.viewBasketLink = document.createElement('a');
        this.viewBasketLink.href = "/min-kurv";
        ClassList.add(this.viewBasketLink, "visuallyhidden");
        this.viewBasketLink.setAttribute("aria-label", "Se indholdet af din kurv");

        // Run initializing code once the DOM is ready.
        onReady(() => this.init(container));
    }

    addEvents() {
        Events.addEvent(this.dom.container, "click", this.handleBasketClick.bind(this));
        Events.addEvent(this.dom.container, "basket-status", event => {
            event.detail.forEach(element => {
                if (element.SectionId === this.sectionId) {
                    this.changeButton("added");
                }
            });
        });
    }

    /**
     * Changes appearance of button
     *
     */
    changeButton(currentlyActive) {
        if (currentlyActive === "add") {
            ClassList.remove(this.dom.container, "addToBasket--added");
            this.dom.buttonText.innerHTML = "Læg i kurv";
            this.dom.container.setAttribute("aria-label", `Læg ${this.sectionHeadline} i kurven`);

            // Remove hidden link to basket, to help screen reader
            this.viewBasketLink.parentNode.removeChild(this.viewBasketLink);

            this.enableCheckbox();
        } else if (currentlyActive === "adding") {
            ClassList.add(this.dom.container, "addToBasket--adding");
            ClassList.add(this.dom.container, "button--no-hover");
            this.dom.buttonText.innerHTML = "Lægger i kurv..";

            this.disableCheckbox();
        } else if(currentlyActive === "confirmed") {
            ClassList.add(this.dom.container, "addToBasket--confirmed");
            this.dom.buttonText.innerHTML = "Lagt i kurv";
        } else {
            ClassList.remove(this.dom.container, "addToBasket--adding");
            ClassList.remove(this.dom.container, "addToBasket--confirmed");
            ClassList.remove(this.dom.container, "button--no-hover");
            ClassList.add(this.dom.container, "addToBasket--added");
            this.dom.buttonText.innerHTML = "Fjern fra kurv";
            this.dom.container.setAttribute("aria-label", `Fjern ${this.sectionHeadline} fra kurv`);

            // Add hidden link to basket, to help screen reader
            this.dom.container.parentNode.insertBefore(this.viewBasketLink, this.dom.container.nextSibling);

            // To also disable if already in basket on load
            this.disableCheckbox();
        }
    }

    /**
     * Enables checkbox by removing disabled attribute
     *
     */
    enableCheckbox() {
        domIterator(this.dom.leaseTypeCheckboxes, element => {
            if (!ClassList.contains(element, "lejerbo-input--disabled")) {
                element.disabled = false;
            }
        });
    }

    /**
     * Disables checkbox by adding disabled attribute
     *
     */
    disableCheckbox() {
        domIterator(this.dom.leaseTypeCheckboxes, element => {
            if (!ClassList.contains(element, "lejerbo-input--disabled")) {
                element.disabled = true;
            }
        });
    }


    /**
     * Handles click on button, and calls appropriate functions
     *
     */
    handleBasketClick() {
        let fetches = [],
            nonChecked = true;

        if (ClassList.contains(this.dom.container, "addToBasket--added")) {
            domIterator(this.dom.leaseTypeCheckboxes, element => {

                // Remove all leaseTypes, regardless of checked or not
                fetches.push(this.removeFromBasket(element.dataset.leaseId));
            });

            // When all fetches are complete, reload window
            Promise.all(fetches).then( () => {
                // window.location.reload();
                triggerCustomEvent(this.dom.minibasket, "loadcontent");

                this.changeButton("add");
            });
        } else {
            domIterator(this.dom.leaseTypeCheckboxes, element => {

                // If checkbox is checked, call addToBasket with appropriate leaseId
                if (element.checked) {
                    fetches.push(this.addToBasket(element.dataset.leaseId));
                    nonChecked = false;
                }
            });

            // If no checkboxes were checked, alert user of the error
            if (nonChecked) {
                this.errorAction();
            } else {
                // Removes the error message
                triggerCustomEvent(this.dom.container, "removemessage");

                // Change button to "adding" state
                this.changeButton("adding");

                // When all fetches are complete, reload window
                Promise.all(fetches).then( () => {
                    // window.location.reload();
                    triggerCustomEvent(this.dom.minibasket, "loadcontent");

                    // Change button to "confirmed" state
                    this.changeButton("confirmed");

                    setTimeout( ()=> {
                        // Change button to "added" state
                        this.changeButton("added");
                    }, 1500);
                });
            }
        }
    }

    /**
     * Adds error message, if no checkbox is checked
     *
     */
    errorAction() {
        formError.addErrorClass(this.dom.container, "Ingen boligtype valgt", "removemessage" );
    }

    /**
     * Makes request to add to basket
     *
     * @return {fetch}
     */
    addToBasket(leaseId) {
        let requestString = apiUrlAdd + '?sectionId=' + this.sectionId + '&leasetypeid=' + leaseId;

        return fetch(requestString, {
            credentials: 'same-origin'
        }).then(this.handleResponse.bind(this)).catch( error => {
            window.console.log('parsing failed', error);
        });
    }

    /**
     * Makes request to remove from basket
     *
     * @return {fetch}
     */
    removeFromBasket(leaseId) {
        let requestString = apiUrlRemove + '?sectionId=' + this.sectionId + '&leasetypeid=' + leaseId;

        return fetch(requestString, {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        })
        .then(this.handleResponse.bind(this))
        .catch( error => {
            window.console.log('parsing failed', error);
        });
    }

    handleResponse(response) {
        let contentType = response.headers.get('content-type');

        if (contentType.includes('application/json')) {
            // return this.handleJSONResponse(response);
            // console.log(contentType, "app/json");
            return;
        } else if (contentType.includes('text/html')) {
            // return this.handleTextResponse(response);
            // console.log(contentType, "text/html");
            return;
        } else {
            throw new Error(`Sorry, content-type ${contentType} not supported`);
        }
    }



    /**
     * The actual initialization function, fired once the DOM is ready.
     *
     * @param {HTMLElement} container
     */
    init(container) {
        this.dom = {
            container, // ES6 allows this syntax instead of writing "container: container"
        };

        // Put your other DOM-specific initializations here, and get your class moving!
        this.dom.buttonText = this.dom.container.querySelector(".button__text");
        this.sectionId = this.dom.container.dataset.sectionid;
        this.sectionHeadline = this.dom.container.dataset.sectionheadline;
        this.dom.leaseTypeCheckboxes = this.dom.container.closest(".box__content").querySelectorAll("input[type='checkbox']");
        this.dom.minibasket = document.getElementById("minibasket");

        this.addEvents();
    }

}


export default AddToBasket;